<script>
  import { onMount } from "svelte";
  import {
    Accordion,
    SectionWrapper,
    Tooltip,
    Select,
  } from "paxray-components-svelte";
  import ButtonIconOnly from "lib/components/buttonIconOnly.svelte";
  import Header from "lib/components/Header.svelte";
  import PaxbotChat from "lib/components/paxbot/PaxbotChat.svelte";
  import SideKpi from "lib/components/sideKPI.svelte";
  import { uiFeatureStore } from "@stores/uiFeatureStore.js";
  import { useNavigate } from "svelte-navigator";
  import ZoomableTreemap from "lib/components/zoomableTreemap/ZoomableTreemap.svelte";
  import { fetchWithSession } from "lib/util/fetchWithSession.js";
  import OnGoingAnalysisPlaceholder from "./OnGoingAnalysisPlaceholder.svelte";
  import { ROUTES } from "@utils/routes.js";

  const metricOptions = [
    {
      key: "active_time_seconds",
      label: "Active Time",
      formatter: formatDuration,
    },
    { key: "users", label: "Users", formatter: formatNumber },
    { key: "clicks", label: "Clicks", formatter: formatNumber },
    {
      key: "copy_time_seconds",
      label: "Copy Time",
      formatter: formatDuration,
    },
    {
      key: "text_time_seconds",
      label: "Text Time",
      formatter: formatDuration,
    },
  ];
  const categoryDeepDiveHelpContext = {
    task: "Explore how software usage is distributed across categories, tools, and teams.",
    starterQuestions: [
      "Which category should I inspect?",
      "Where is harmonization potential?",
      "Which tools overlap across teams?",
      "How concentrated is this category?",
    ],
  };
  const levelOptions = ["Categories", "Tools", "Teams"];
  const metricMenuOptions = [
    { text: "Active Time", icon: "time_dark" },
    { text: "Users", icon: "user" },
    { text: "Clicks", icon: "mouseclicks_dark" },
    { text: "Copy Time", icon: "copy_dark" },
    { text: "Text Time", icon: "keyboard_dark" },
  ];
  const minimumShareOptions = [0.5, 1, 2, 5, 10, 20];
  const categoryColors = [
    "#b7e3bd",
    "#e5e8a8",
    "#abcfe8",
    "#e6a7ad",
    "#d8c4f1",
    "#f2c6a0",
  ];
  const navigate = useNavigate();

  let categories = [];
  let summary = {};
  let harmonizationCandidates = [];
  let projects = [];
  let selectedProject = "";
  // The on-going analysis remains in the codebase but is temporarily not exposed.
  const sourceMode = 0;
  let selectedMetricIndex = 0;
  let selectedLevel = 0;
  let currentPath = ["Software Categories"];
  let loading = true;
  let error = "";
  let teamBreakdownReady = false;
  let fullscreen = false;
  let paxbotOpen = false;
  let treemap;
  let previousMetricIndex = selectedMetricIndex;
  let categoryMinimumShare = 1;

  $: selectedMetric = metricOptions[selectedMetricIndex];
  $: demoCategoryData = Boolean(
    $uiFeatureStore.platformFeatures.demoSoftwareIntelligence,
  );
  $: treeData = buildTree(categories, selectedMetric.key, categoryMinimumShare);
  $: categoryPortfolioRows = buildCategoryPortfolioRows(
    categories,
    selectedMetric.key,
    categoryMinimumShare,
    demoCategoryData,
  );
  $: categoryColorMap = Object.fromEntries(
    categoryPortfolioRows.map((row) => [
      String(row.category || "")
        .trim()
        .toLowerCase(),
      row.color,
    ]),
  );
  $: selectedCategory = findSelectedCategory(categories, currentPath);
  $: selectedTool = findSelectedTool(selectedCategory, currentPath);
  $: selectedTeam = findSelectedTeam(selectedTool, currentPath);
  $: selection = getSelection(
    selectedCategory,
    selectedTool,
    selectedTeam,
    summary,
    categoryShare,
    toolShare,
    teamShare,
  );
  $: scopedCandidates = harmonizationCandidates.filter(
    (candidate) =>
      (!selectedCategory || candidate.category === selectedCategory.category) &&
      (!selectedTool || candidate.tools.includes(selectedTool.application)),
  );
  $: relevantCandidates = scopedCandidates.slice(0, 6);
  $: harmonizationAssessment = buildHarmonizationAssessment(scopedCandidates);
  $: metricSelectorOptions = metricMenuOptions.map((option, index) => ({
    value: index,
    text: option.text,
  }));
  $: categoryShare = summary[selectedMetric.key]
    ? (Number(selectedCategory?.[selectedMetric.key] || 0) /
        Number(summary[selectedMetric.key])) *
      100
    : 0;
  $: toolShare = selectedCategory?.[selectedMetric.key]
    ? (Number(selectedTool?.[selectedMetric.key] || 0) /
        Number(selectedCategory[selectedMetric.key])) *
      100
    : 0;
  $: teamShare = selectedTool?.[selectedMetric.key]
    ? (Number(selectedTeam?.[selectedMetric.key] || 0) /
        Number(selectedTool[selectedMetric.key])) *
      100
    : 0;
  $: if (selectedMetricIndex !== previousMetricIndex) {
    previousMetricIndex = selectedMetricIndex;
    selectedLevel = 0;
    currentPath = ["Software Categories"];
  }

  onMount(async () => {
    await Promise.all([loadProjects(), loadDeepDive()]);
  });

  async function loadProjects() {
    try {
      const response = await fetchWithSession("/api/monitoring/projects");
      if (response.ok) projects = await response.json();
    } catch (loadError) {
      error = loadError.message;
    }
  }

  async function loadDeepDive() {
    loading = true;
    error = "";
    try {
      const query = selectedProject
        ? `?project_id=${encodeURIComponent(selectedProject)}`
        : "";
      const response = await fetchWithSession(
        `/api/monitoring/category-deep-dive${query}`,
      );
      const result = await response.json();
      if (!response.ok) {
        throw new Error(
          result.message || "Category deep dive could not be loaded.",
        );
      }
      categories = (result.categories || []).map((category) => ({
        ...category,
        category: normalizeCategoryName(category.category),
      }));
      summary = result.summary || {};
      harmonizationCandidates = result.harmonization_candidates || [];
      teamBreakdownReady = !!result.team_breakdown_ready;
      selectedLevel = 0;
      currentPath = ["Software Categories"];
    } catch (loadError) {
      error = loadError.message || "Category deep dive could not be loaded.";
    } finally {
      loading = false;
    }
  }

  function buildTree(items, metricKey, minimumShare) {
    const total = Number(summary[metricKey] || 0);
    const minimumValue = total * (Number(minimumShare) / 100);
    const categoriesInScope = items.filter(
      (category) =>
        isUncategorizedCategory(category) ||
        (!isOthersCategory(category) &&
          Number(category[metricKey] || 0) >= minimumValue),
    );
    const categoryOthersValue = items
      .filter(
        (category) =>
          !isUncategorizedCategory(category) &&
          (isOthersCategory(category) || !categoriesInScope.includes(category)),
      )
      .reduce((sum, category) => sum + Number(category[metricKey] || 0), 0);

    const children = categoriesInScope.map((category) => ({
      name: displayCategoryName(category),
      children: buildThresholdNodes(
        category.tools,
        metricKey,
        minimumShare,
        (tool) => tool.application,
      ),
    }));

    if (categoryOthersValue > 0) {
      children.push({ name: "Others", value: categoryOthersValue });
    }

    return { name: "Software Categories", children };
  }

  // Die Schwelle gilt je Ebene relativ zu deren Summe: ein Werkzeug zaehlt als
  // eigenes Feld, wenn es X % seiner Kategorie hat, nicht X % des Ganzen. Sonst
  // zeigte eine kleine Kategorie beim Eintauchen nur "Others 100 %".
  function buildThresholdNodes(items, metricKey, minimumShare, getName) {
    const levelTotal = items.reduce(
      (sum, item) => sum + Number(item[metricKey] || 0),
      0,
    );
    const minimumValue = levelTotal * (Number(minimumShare) / 100);
    const visibleItems = items.filter(
      (item) => Number(item[metricKey] || 0) >= minimumValue,
    );
    const othersValue = items
      .filter((item) => !visibleItems.includes(item))
      .reduce((sum, item) => sum + Number(item[metricKey] || 0), 0);

    const nodes = visibleItems.map((item) => {
      const teams = buildThresholdNodes(
        item.team_breakdown || [],
        metricKey,
        minimumShare,
        (team) => team.team,
      );
      return teams.length
        ? { name: getName(item), children: teams }
        : { name: getName(item), value: Number(item[metricKey] || 0) };
    });

    if (othersValue > 0) {
      nodes.push({ name: "Others", value: othersValue });
    }
    return nodes;
  }

  function buildCategoryPortfolioRows(items, metricKey, minimumShare, demo) {
    const total = Number(summary[metricKey] || 0);
    const minimumValue = total * (Number(minimumShare) / 100);
    const unassigned = items.find(isUncategorizedCategory);
    const visible = items.filter(
      (category) =>
        !isUncategorizedCategory(category) &&
        !isOthersCategory(category) &&
        Number(category[metricKey] || 0) >= minimumValue,
    );
    const othersValue = items
      .filter(
        (category) =>
          !isUncategorizedCategory(category) &&
          (isOthersCategory(category) || !visible.includes(category)),
      )
      .reduce((sum, category) => sum + Number(category[metricKey] || 0), 0);
    const rows = visible.map((category, index) => ({
      ...category,
      color: categoryColors[index % categoryColors.length],
      share: total ? (Number(category[metricKey] || 0) / total) * 100 : 0,
      rowType: "category",
    }));

    if (othersValue > 0) {
      rows.push({
        category: "Others",
        color: "#c9ced6",
        share: total ? (othersValue / total) * 100 : 0,
        active_time_seconds: 0,
        rowType: "others",
      });
    }

    if (unassigned || demo) {
      const unassignedShare = unassigned
        ? (Number(unassigned.tool_count || 0) /
            Number(summary.application_count || 0)) *
          100
        : 7;
      rows.push({
        category: "Apps without Category",
        color: "#8a929b",
        share: unassignedShare,
        active_time_seconds: 0,
        rowType: "unassigned",
        demo: !unassigned,
      });
    }

    return rows;
  }

  function findSelectedCategory(categories, currentPath) {
    return categories.find((category) =>
      currentPath.includes(category.category),
    );
  }

  function findSelectedTool(category, currentPath) {
    return category?.tools.find((tool) =>
      currentPath.includes(tool.application),
    );
  }

  function findSelectedTeam(tool, currentPath) {
    return tool?.team_breakdown?.find((team) =>
      currentPath.includes(team.team),
    );
  }

  function getSelection(
    selectedCategory,
    selectedTool,
    selectedTeam,
    summary,
    categoryShare,
    toolShare,
    teamShare,
  ) {
    if (selectedTeam) {
      return {
        level: "Team",
        name: selectedTeam.team,
        metrics: selectedTeam,
        share: teamShare,
      };
    }
    if (selectedTool) {
      return {
        level: "Tool",
        name: selectedTool.application,
        metrics: selectedTool,
        share: toolShare,
      };
    }
    if (selectedCategory) {
      return {
        level: "Category",
        name: selectedCategory.category,
        metrics: selectedCategory,
        share: categoryShare,
      };
    }
    return {
      level: "Portfolio",
      name: "All Software Categories",
      metrics: summary,
      share: 100,
    };
  }

  function formatDuration(seconds) {
    const hours = Number(seconds || 0) / 3600;
    if (hours >= 1000) {
      return `${new Intl.NumberFormat("en", {
        maximumFractionDigits: 1,
      }).format(hours / 1000)}k h`;
    }
    return `${new Intl.NumberFormat("en", {
      maximumFractionDigits: hours < 10 ? 1 : 0,
    }).format(hours)} h`;
  }

  function formatNumber(value) {
    return new Intl.NumberFormat("en", { notation: "compact" }).format(
      Number(value || 0),
    );
  }

  function normalizeCategoryName(value) {
    const normalized = String(value || "")
      .trim()
      .toLowerCase();
    return ["weitere anwendungen", "andere anwendungen", "others"].includes(
      normalized,
    )
      ? "Others"
      : value || "Others";
  }

  function isUncategorizedCategory(category) {
    return (
      String(category?.category || "")
        .trim()
        .toLowerCase() === "uncategorized"
    );
  }

  function isOthersCategory(category) {
    return (
      String(category?.category || "")
        .trim()
        .toLowerCase() === "others"
    );
  }

  function displayCategoryName(category) {
    return isUncategorizedCategory(category)
      ? "Apps without Category"
      : category.category;
  }

  function categorySharePercent(category) {
    const total = Number(summary[selectedMetric.key] || 0);
    return total
      ? ((Number(category[selectedMetric.key] || 0) / total) * 100).toFixed(0)
      : "0";
  }

  function typeLabel(value) {
    return String(value || "Unclassified")
      .replace(/[-_]/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  }

  function selectCategory(category) {
    currentPath = [category.category];
    selectedLevel = 1;
  }

  function openAppnameMapping() {
    const projectQuery = selectedProject
      ? `?idproject=${encodeURIComponent(selectedProject)}`
      : "";
    navigate(`${ROUTES.dam.appnamemapping}${projectQuery}`);
  }

  function selectionTools() {
    return (
      selectedCategory?.tools ||
      categories.flatMap((category) => category.tools)
    );
  }

  function candidateReason(candidate) {
    const overlap = candidate.overlap_teams.length;
    return `${candidate.team_overlap_percent}% team overlap across ${overlap} team${overlap === 1 ? "" : "s"}.`;
  }

  function buildHarmonizationAssessment(candidates) {
    const tools = new Set(candidates.flatMap((candidate) => candidate.tools));
    const teams = new Set(
      candidates.flatMap((candidate) => candidate.overlap_teams),
    );
    return {
      candidates: candidates.length,
      tools: tools.size,
      teams: teams.size,
      highestOverlap: Math.max(
        0,
        ...candidates.map((candidate) => candidate.team_overlap_percent),
      ),
    };
  }

  async function requestPaxbotAnswer(question, history, responseLanguage) {
    const response = await fetchWithSession("/api/damprojects/paxbot/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        question,
        history: history.slice(-2),
        context: {
          page: "software category deep dive",
          responseLanguage,
          metric: selectedMetric.label,
          level: levelOptions[selectedLevel],
          selection: {
            level: selection.level,
            name: selection.name,
            displayValue: selectedMetric.formatter(
              selection.metrics[selectedMetric.key],
            ),
            sharePercent: Number(selection.share || 0).toFixed(1),
          },
          category: selectedCategory,
          tool: selectedTool,
          visibleTools: selectionTools()
            .slice(0, 15)
            .map((tool) => ({
              application: tool.application,
              type: tool.type,
              value: selectedMetric.formatter(tool[selectedMetric.key]),
              teams: tool.teams,
            })),
          harmonizationCandidates: relevantCandidates,
        },
      }),
    });
    const result = await response.json();
    if (!response.ok || result.status === "error") {
      throw new Error(result.message || "Paxbot request failed");
    }
    return result.answer;
  }
</script>

<Header
  section="Digital Twin"
  projectNameOverride="Digital Twin"
  projectHref={ROUTES.dam.root}
  area="Software Intelligence"
  areaClickable={false}
  breadcrumb="Category Deep Dive"
  tooltipText="Category Deep Dive visualizes software usage by category, tool and team. Use the project selector to scope the data, switch metrics to compare time or volume, and drill into the treemap to find overlap and harmonization potential."
></Header>

{#if sourceMode === 0}
  <SectionWrapper
    {fullscreen}
    fitScreen
    {loading}
    loadingTitle="Loading category data."
    loadingHint="Please wait while Category Deep Dive is prepared."
    helpContext={categoryDeepDiveHelpContext}
  >
    <div class="deep-dive-main" slot="main">
      <PaxbotChat
        bind:open={paxbotOpen}
        heading="Pax AI"
        subheading="Category Deep Dive Assistant"
        requestAnswer={requestPaxbotAnswer}
        starterQuestions={[
          "Which category should I inspect?",
          "Where is harmonization potential?",
          "Which tools overlap across teams?",
          "How concentrated is this category?",
        ]}
      />

      {#if !teamBreakdownReady && categories.length}
        <div class="team-data-notice">
          Category and tool levels are available. Exact team shares require a
          current precompute and delta transfer.
        </div>
      {/if}

      <div class="treemap-controls">
        <div class="treemap-selector-group">
          <div class="treemap-filter-field">
            <Select
              items={metricSelectorOptions}
              bind:selected={selectedMetricIndex}
              onchange={() =>
                (selectedMetricIndex = Number(selectedMetricIndex))}
              color="dark"
              labelText="Metric"
            />
          </div>
        </div>
        <div class="treemap-actions">
          <ButtonIconOnly
            medium
            icon={fullscreen ? "fullscreen_off" : "fullscreen"}
            title={fullscreen ? "Exit fullscreen" : "Fullscreen"}
            on:click={() => (fullscreen = !fullscreen)}
          />
        </div>
      </div>

      <div class="treemap-surface">
        {#if error}
          <div class="empty-state error">{error}</div>
        {:else if !loading && !categories.length}
          <div class="empty-state">
            Transfer calculated software results to start the category deep
            dive.
          </div>
        {:else}
          {#key treeData}
            <ZoomableTreemap
              bind:this={treemap}
              bind:currentApp={currentPath}
              bind:level={selectedLevel}
              {treeData}
              {loading}
              formatfunction={selectedMetric.formatter}
              responsiveLabels
              pinOthersLast
              nodeColors={categoryColorMap}
            />
          {/key}
        {/if}
      </div>
    </div>

    <div class="deep-dive-menu" slot="menu">
      <Accordion heading="Current Selection" open>
        <div class="kpi-list">
          <SideKpi
            name={selectedMetric.label}
            value={selectedMetric.formatter(
              selection.metrics[selectedMetric.key],
            )}
          />
          <SideKpi
            name="Applications"
            value={selectedCategory
              ? selectedCategory.tool_count
              : summary.application_count || 0}
          />
          <SideKpi
            name="Application-user assignments"
            value={formatNumber(selection.metrics.users)}
          />
          <SideKpi
            name="Copy time"
            value={formatDuration(selection.metrics.copy_time_seconds)}
          />
          {#if selectedCategory}
            <SideKpi
              name="Top-tool concentration"
              value={Number(selectedCategory.top_tool_share || 0).toFixed(0)}
              unit="%"
            />
          {/if}
        </div>
      </Accordion>

      <Accordion heading="Category Portfolio" open={!selectedCategory}>
        <div class="category-portfolio-settings">
          <label for="category-minimum-share"
            >Minimum share to show isolated</label
          >
          <select
            id="category-minimum-share"
            value={categoryMinimumShare}
            on:change={(event) => {
              categoryMinimumShare = Number(event.target.value);
              selectedLevel = 0;
              currentPath = ["Software Categories"];
            }}
          >
            {#each minimumShareOptions as option}
              <option value={option}>{option}%</option>
            {/each}
          </select>
        </div>
        <div class="category-list">
          {#each categoryPortfolioRows as row}
            {#if row.rowType === "category"}
              <button
                type="button"
                class:active={selectedCategory?.category === row.category}
                on:click={() => selectCategory(row)}
              >
                <span class="category-name">
                  <span
                    class="category-color-block"
                    style={`background: ${row.color}`}
                  ></span>
                  <strong>{row.category}</strong>
                </span>
                <Tooltip
                  text={`${formatDuration(row.active_time_seconds)} active time · ${row.share.toFixed(0)}% share`}
                  showIcon={false}
                >
                  <span slot="trigger" class="category-share">
                    {row.share.toFixed(0)}%
                  </span>
                </Tooltip>
              </button>
            {:else}
              <div
                class="category-portfolio-row"
                class:unassigned-category-row={row.rowType === "unassigned"}
              >
                {#if row.rowType === "unassigned"}
                  <div class="category-unassigned-value category-name">
                    <strong>{row.category}</strong>
                    <Tooltip
                      text={`${row.demo ? "Demo: " : ""}share of applications without an assigned category.`}
                      showIcon={false}
                    >
                      <span slot="trigger" class="category-share">
                        {row.share.toFixed(0)}%
                      </span>
                    </Tooltip>
                  </div>
                  <div class="category-row-actions">
                    <ButtonIconOnly
                      icon="category"
                      small
                      title="Open app name mapping"
                      on:click={openAppnameMapping}
                    />
                  </div>
                {:else}
                  <span class="category-name">
                    <strong>{row.category}</strong>
                  </span>
                  <Tooltip
                    text={`Aggregated categories below ${categoryMinimumShare}% · ${row.share.toFixed(0)}% share`}
                    showIcon={false}
                  >
                    <span slot="trigger" class="category-share">
                      {row.share.toFixed(0)}%
                    </span>
                  </Tooltip>
                {/if}
              </div>
            {/if}
          {/each}
        </div>
      </Accordion>

      {#if selectedTool && teamBreakdownReady}
        <Accordion heading="Team Contribution" open>
          <div class="team-list">
            {#each selectedTool.team_breakdown || [] as team}
              <div>
                <span>
                  <strong>{team.team}</strong>
                  <small>{formatNumber(team.users)} users</small>
                </span>
                <span>
                  <strong
                    >{selectedMetric.formatter(
                      team[selectedMetric.key],
                    )}</strong
                  >
                  <small>
                    {selectedTool[selectedMetric.key]
                      ? (
                          (team[selectedMetric.key] /
                            selectedTool[selectedMetric.key]) *
                          100
                        ).toFixed(0)
                      : 0}%
                  </small>
                </span>
              </div>
            {/each}
          </div>
        </Accordion>
      {/if}
    </div>
  </SectionWrapper>
{:else}
  <OnGoingAnalysisPlaceholder
    title="On-going Category Deep Dive"
    description="Track how continuously observed software categories, tools and team contributions evolve over time."
  />
{/if}

<style>
  .deep-dive-main {
    position: relative;
    display: flex;
    width: 100%;
    min-width: 100%;
    height: 100%;
    min-height: 0;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
  }

  .treemap-surface {
    width: 100%;
    height: auto;
    min-height: 0;
    flex: 1 1 auto;
    box-sizing: border-box;
    padding-bottom: 0.75rem;
    padding-top: 0.15rem;
    overflow: hidden;
  }

  .treemap-controls {
    display: flex;
    width: 100%;
    min-height: 2.5rem;
    flex: 0 0 auto;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.25rem 0.5rem 0.5rem;
    box-sizing: border-box;
  }

  .treemap-selector-group,
  .treemap-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }

  .treemap-filter-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .treemap-filter-field :global(.custom_select) {
    min-width: 10rem;
  }

  .treemap-controls :global(.button_wrapper) {
    width: 2.6rem;
    height: 2.6rem;
  }

  .treemap-surface :global(.treemap_wrapper) {
    min-height: 0;
    padding-bottom: 0;
  }

  .treemap-surface :global(.chart) {
    height: auto;
    min-height: 0;
    flex: 1 1 auto;
  }

  .deep-dive-main :global(.paxbot_chat_window) {
    position: absolute !important;
    z-index: 50 !important;
    right: 1rem !important;
    bottom: 5.85rem !important;
  }

  .deep-dive-main :global(.paxbot_chat_mascot) {
    position: absolute !important;
    z-index: 51 !important;
    right: 1.55rem !important;
    bottom: -0.65rem !important;
  }

  .team-data-notice {
    position: absolute;
    z-index: 30;
    top: 3.65rem;
    left: 0.5rem;
    max-width: 30rem;
    padding: 0.55rem 0.75rem;
    border-left: 0.2rem solid var(--pax-orange);
    border-radius: 0.3rem;
    background: #fff4ef;
    font-size: inherit;
    box-shadow: 0 0.3rem 0.8rem rgba(24, 28, 34, 0.08);
  }

  .deep-dive-menu {
    display: flex;
    height: calc(100vh - 6rem);
    flex-direction: column;
    gap: 1rem;
    overflow-y: auto;
    scrollbar-width: none;
    font-size: inherit;
  }

  .deep-dive-menu::-webkit-scrollbar {
    display: none;
  }

  .deep-dive-menu :global(.accordion_heading) {
    font-size: inherit;
  }

  .team-list small {
    color: #6f7681;
    font-size: inherit;
  }

  .kpi-list,
  .category-list,
  .team-list {
    display: grid;
    gap: 0.5rem;
  }

  .category-portfolio-settings {
    display: flex;
    min-height: 2.25rem;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
    padding: 0.45rem 0.65rem;
    border-radius: 0.25rem;
    background: var(--pax-smoke);
  }

  .category-portfolio-settings label {
    min-width: 0;
    font: inherit;
  }

  .category-portfolio-settings select {
    height: 2rem;
    min-width: 4.25rem;
    flex: 0 0 auto;
    border: 1px solid #d9d9d9;
    border-radius: 0.5rem;
    background: var(--pax-white);
    color: var(--pax-black);
    font: inherit;
    padding: 0 0.5rem;
  }

  .category-list {
    max-height: none;
    overflow: visible;
  }

  .category-list button,
  .team-list > div {
    display: flex;
    width: 100%;
    min-height: 2.25rem;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.45rem 0.65rem;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    background: var(--pax-smoke);
    color: var(--pax-black);
    text-align: left;
  }

  .category-list button {
    cursor: pointer;
    font-family: inherit;
    font-size: inherit;
    font-weight: inherit;
    line-height: inherit;
  }

  .deep-dive-menu :global(.kpi_wrapper) {
    height: 2.25rem;
    padding: 0.45rem 0.65rem;
    border-radius: 0.25rem;
  }

  .category-list button:hover,
  .category-list button.active {
    border-color: var(--pax-orange);
  }

  .category-list button > span,
  .team-list > div > span {
    display: flex;
    min-width: 0;
    flex-direction: column;
    gap: 0.12rem;
  }

  .category-name {
    flex-direction: row !important;
    align-items: center;
    gap: 0.5rem !important;
  }

  .category-unassigned-value {
    gap: 0.35rem !important;
  }

  .category-color-block {
    width: 0.85rem;
    height: 0.85rem;
    flex: 0 0 0.85rem;
    border: 1px solid rgba(28, 28, 32, 0.25);
    border-radius: 0.15rem;
  }

  .category-portfolio-row {
    display: flex;
    min-height: 2.25rem;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem;
    padding: 0.45rem 0.65rem;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    background: var(--pax-smoke);
    color: var(--pax-black);
  }

  .unassigned-category-row {
    margin-top: 0.5rem;
    padding: 0.5rem 0 0;
    border: 0;
    border-top: 1px solid #d9d9d9;
    border-radius: 0;
    background: transparent;
  }

  .category-row-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 0 0 auto;
  }

  .category-row-actions :global(.button_wrapper) {
    width: 2rem;
    height: 2rem;
  }

  .category-list button > span:last-child,
  .team-list > div > span:last-child {
    flex: 0 0 auto;
    align-items: flex-end;
  }

  .category-list button :global(.tooltip) {
    flex: 0 0 auto;
  }

  .category-share {
    color: var(--pax-black);
    font-size: inherit;
    font-weight: 700;
    text-align: right;
  }

  .deep-dive-menu :global(.kpi_wrapper .type),
  .deep-dive-menu :global(.kpi_wrapper .value) {
    font-size: inherit;
  }

  .category-list strong,
  .team-list strong {
    max-width: 12rem;
    overflow: hidden;
    font-size: inherit;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .category-list strong {
    font-weight: 400;
  }

  .empty-state {
    display: grid;
    width: 100%;
    height: 100%;
    place-items: center;
    color: #6f7681;
  }

  .empty-state.error {
    color: #da1e28;
  }
</style>
